import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';

interface PathCard {
  title: string;
  body: string[];
  microLine: string;
  ctaLabel: string;
  ctaHref: string;
  primary?: boolean;
  scarcity?: boolean;
}

interface Pillar {
  title: string;
  body: string;
  linkLabel: string;
  linkHref: string;
}

const PATHS: PathCard[] = [
  {
    title: 'Start with a premium domain.',
    body: [
      'Hand-picked names for service businesses—branding, legal, accounting, HR, marketing, design, and more.',
      'Buy, lease, or lease-to-own with secure Stripe checkout.',
      'Curated to feel like real assets, not random keyword lists.',
    ],
    microLine: "If your next big client hears about you but can't remember your domain, you've already lost the moment.",
    ctaLabel: 'Browse domains →',
    ctaHref: '/domains',
    primary: true,
  },
  {
    title: 'Already have a domain?',
    body: [
      'Keep the name you love and fix the parts that are holding you back.',
      'Get positioning, brand, and a website that actually books consults instead of just sitting there.',
      'One partner for design, dev, and basic launch systems.',
    ],
    microLine: 'A strong domain paired with a confusing site still leaks consults every week.',
    ctaLabel: 'View launch services →',
    ctaHref: '/services',
  },
  {
    title: 'Turnkey Attain Domains launch.',
    body: [
      'Domain, brand, website, and simple systems—planned and built together in one focused project.',
      "Work directly with an operator who's actually built and sold service businesses.",
      'One build, one owner, one clear direction.',
    ],
    microLine: 'I only take a few full launch clients at a time so I can actually partner with you.',
    ctaLabel: 'See Launch Studio →',
    ctaHref: '/services#launch-studio',
    scarcity: true,
  },
];

const PILLARS: Pillar[] = [
  {
    title: 'Branding & Identity',
    body: 'Frameworks and assets—not just a logo file—so your brand matches your pricing and positioning.',
    linkLabel: 'See packages →',
    linkHref: '/services',
  },
  {
    title: 'Website & Funnel',
    body: 'Conversion-led architecture, clear copy, and a simple path to book a call or start a project.',
    linkLabel: 'See packages →',
    linkHref: '/services',
  },
  {
    title: 'Professional Operations',
    body: "Entity, basic legal, and people-ops foundations so you don't look or feel DIY when real clients show up.",
    linkLabel: 'See packages →',
    linkHref: '/services',
  },
  {
    title: 'Launch & Lead Plan',
    body: 'A practical 30–60 day plan to start the right conversations and fill your calendar, not just your inbox.',
    linkLabel: 'See packages →',
    linkHref: '/services',
  },
];

export function HomePathsAndPillars() {
  const headingReveal = useRevealOnce({ amount: 0.25 });
  const pathsReveal = useRevealOnce({ amount: 0.25 });
  const pillarsReveal = useRevealOnce({ amount: 0.25 });

  return (
    <section id="paths" className="bg-white py-16 md:py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Heading and intro */}
          <motion.div
            ref={headingReveal.ref}
            className="text-center mb-12 md:mb-16"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.h2
              variants={getGridItemVariants(0)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-slate-900"
            >
              Choose how you want to build.
            </motion.h2>
            <motion.p
              variants={getGridItemVariants(1)}
              className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
            >
              Whether you're starting from a blank slate or sitting on a great domain, there's a clear next step. Pick the path that matches where you are right now.
            </motion.p>
          </motion.div>

          {/* Three path cards */}
          <motion.div
            ref={pathsReveal.ref}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16 md:mb-20"
            variants={gridContainerVariants}
            initial="hidden"
            animate={pathsReveal.controls}
          >
            {PATHS.map((path, index) => (
              <motion.div
                key={path.title}
                variants={getGridItemVariants(index)}
                className="group"
              >
                <div
                  className={`relative h-full rounded-2xl bg-white border shadow-sm p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${
                    path.primary
                      ? 'border-2 border-blue-500/30 hover:border-blue-500/50 hover:shadow-blue-500/10'
                      : 'border border-slate-200 hover:border-blue-500/30 hover:shadow-blue-500/5'
                  }`}
                >
                  {/* Recommended badge for primary card */}
                  {path.primary && (
                    <div className="absolute top-4 right-4 px-3 py-1 text-xs font-semibold text-blue-600 bg-blue-50 rounded-full">
                      Recommended
                    </div>
                  )}

                  {/* Card content */}
                  <div className={path.primary ? 'pr-16' : ''}>
                    <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-slate-900">
                      {path.title}
                    </h3>
                    <div className="space-y-3 mb-6">
                      {path.body.map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-sm md:text-base text-slate-600 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    <p className="text-xs md:text-sm text-slate-500 italic mb-6 border-t border-slate-200 pt-4">
                      {path.microLine}
                    </p>
                    <Link
                      to={path.ctaHref}
                      className="inline-flex items-center text-base md:text-lg font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      {path.ctaLabel}
                    </Link>
                  </div>

                  {/* Gradient border glow on hover */}
                  <div
                    className={`absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none blur-sm ${
                      path.primary
                        ? 'bg-gradient-to-r from-blue-500/20 via-blue-500/30 to-blue-500/20'
                        : 'bg-gradient-to-r from-blue-500/10 via-blue-500/20 to-blue-500/10'
                    }`}
                    aria-hidden="true"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Service Pillars */}
          <motion.div
            ref={pillarsReveal.ref}
            className="mt-10"
            variants={gridContainerVariants}
            initial="hidden"
            animate={pillarsReveal.controls}
          >
            <motion.div
              variants={getGridItemVariants(0)}
              className="text-center mb-8 md:mb-10"
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900">
                Wrap your domain in the foundations of a real business.
              </h3>
              <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto">
                Every strong launch is built on these four pillars—I can help you with one, some, or all of them.
              </p>
            </motion.div>

            <motion.div
              variants={gridContainerVariants}
              initial="hidden"
              animate={pillarsReveal.controls}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            >
              {PILLARS.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  variants={getGridItemVariants(index + 1)}
                  className="group"
                >
                  <div className="h-full rounded-xl bg-white border border-slate-200 p-5 transition-all duration-300 hover:translate-y-[-2px] hover:border-blue-500/30 hover:shadow-md">
                    <h4 className="text-lg md:text-xl font-bold mb-3 text-slate-900">
                      {pillar.title}
                    </h4>
                    <p className="text-sm md:text-base text-slate-600 mb-4 leading-relaxed">
                      {pillar.body}
                    </p>
                    <Link
                      to={pillar.linkHref}
                      className="inline-flex items-center text-sm md:text-base font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      {pillar.linkLabel}
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

